import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ScenarioConfig, ScenarioIssue, SystemTool, SystemToolMeta } from './types';
import { bindableFacts, omitKeys, renameToolAt, setIn, switchSystemTemplate } from './scenarioDraft';
import { RequirementsEditor } from './RequirementsEditor';
import { IssueList, NameField, Section, SelectField, TextField, Toggle } from './ui';

interface Props {
  config: ScenarioConfig;
  index: number;
  onChange: (config: ScenarioConfig) => void;
  issues: ScenarioIssue[];
  systemTools: SystemToolMeta[];
  agentToolEnabled: boolean;
}

export const SystemToolEditor: React.FC<Props> = ({
  config,
  index,
  onChange,
  issues,
  systemTools,
  agentToolEnabled,
}) => {
  const { t } = useTranslation();
  const tool = config.tools[index] as SystemTool;
  const ptr = `/tools/${index}`;
  const meta = systemTools.find(item => item.id === tool.system);
  const update = (path: (string | number)[], value: unknown) =>
    onChange(setIn(config, ['tools', index], setIn(tool, path, value)));
  const label = (key: string, fallback: string) =>
    t(`agents.scenario.systemTools.${tool.system}.${key}`, { defaultValue: fallback });
  const setBinding = (param: string, fact: string) =>
    update(['bind'], fact ? { ...tool.bind, [param]: fact } : omitKeys(tool.bind, param));

  return (
    <div className="space-y-3">
      <IssueList issues={issues} pointer={ptr} />
      {!agentToolEnabled && (
        <div className="flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 p-3 text-xs text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-300">
          <span className="material-icons text-base">warning</span>
          <span>{t('agents.scenario.systemTools.disabledInAgent')}</span>
        </div>
      )}

      <Section title={t('agents.scenario.sectionGeneral')}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <NameField
            label={t('agents.scenario.technicalName')}
            value={tool.id}
            onCommit={value => onChange(renameToolAt(config, index, value))}
            issues={issues}
            pointer={`${ptr}/id`}
          />
          <TextField
            label={t('agents.scenario.toolTitle')}
            value={tool.title}
            onChange={value => update(['title'], value)}
            issues={issues}
            pointer={`${ptr}/title`}
          />
          <SelectField
            label={t('agents.scenario.systemTools.system')}
            value={tool.system}
            options={systemTools.map(item => ({
              value: item.id,
              label: t(`agents.scenario.systemTools.${item.id}.title`, { defaultValue: item.title }),
            }))}
            onChange={value => {
              const next = systemTools.find(item => item.id === value);
              onChange(next ? switchSystemTemplate(config, index, next) : setIn(config, ['tools', index, 'system'], value));
            }}
            issues={issues}
            pointer={`${ptr}/system`}
          />
        </div>
        <TextField
          label={t('agents.scenario.descriptionForAi')}
          value={tool.description}
          onChange={value => update(['description'], value)}
          issues={issues}
          pointer={`${ptr}/description`}
          multiline
        />
        <Toggle label={t('agents.scenario.toolEnabled')} checked={tool.enabled} onChange={value => update(['enabled'], value)} />
      </Section>

      <Section title={t('agents.scenario.sectionRequirements')} issues={issues} pointer={`${ptr}/requires`}>
        <RequirementsEditor
          config={config}
          tool={tool}
          pointer={`${ptr}/requires`}
          onChange={requires => update(['requires'], requires)}
          issues={issues}
        />
      </Section>

      <Section
        title={t('agents.scenario.systemTools.bindings')}
        subtitle={t('agents.scenario.systemTools.bindingsHint')}
        issues={issues}
        pointer={`${ptr}/bind`}
      >
        <IssueList issues={issues} pointer={`${ptr}/bind`} />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {(meta?.params ?? [])
            .filter(param => param.bindable)
            .map(param => (
              <SelectField
                key={param.name}
                label={`${label(`params.${param.name}`, param.description)}${param.required ? ' *' : ''}`}
                value={tool.bind[param.name] ?? ''}
                options={[
                  ...(param.required ? [] : [{ value: '', label: t('agents.scenario.systemTools.notBound') }]),
                  ...bindableFacts(config, param).map(f => ({ value: f.name, label: f.name })),
                ]}
                onChange={value => setBinding(param.name, value)}
                issues={issues}
                pointer={`${ptr}/bind/${param.name}`}
                placeholder={t('agents.scenario.selectPlaceholder')}
              />
            ))}
        </div>
      </Section>

      <Section title={t('agents.scenario.systemTools.behaviour')}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <SelectField
            label={t('agents.scenario.systemTools.successWhen')}
            value={tool.success_when}
            options={(meta?.success_modes ?? []).map(mode => ({
              value: mode,
              label: label(`successModes.${mode}`, mode),
            }))}
            onChange={value => update(['success_when'], value)}
            issues={issues}
            pointer={`${ptr}/success_when`}
          />
          {meta?.supports_corrections && (
            <div className="self-end pb-2">
              <Toggle
                label={label('applyCorrections', t('agents.scenario.systemTools.applyCorrections'))}
                checked={tool.apply_corrections}
                onChange={value => update(['apply_corrections'], value)}
              />
            </div>
          )}
        </div>
        {meta?.supports_corrections && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {tool.apply_corrections
              ? t('agents.scenario.systemTools.applyCorrectionsOn')
              : t('agents.scenario.systemTools.applyCorrectionsOff')}
          </p>
        )}
        <IssueList issues={issues} pointer={`${ptr}/apply_corrections`} />
      </Section>
    </div>
  );
};
